/**
 * Runtime performance tier for Codex website.
 * Drives WebGL, grain, cursor, and animation budgets.
 */
export type PerfTier = 'high' | 'low' | 'minimal';

declare global {
  interface Navigator {
    deviceMemory?: number;
    connection?: { saveData?: boolean; effectiveType?: string };
  }
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isCoarseMobile(): boolean {
  return window.matchMedia('(pointer: coarse) and (max-width: 900px)').matches;
}

function hasWeakHardware(): boolean {
  const cores = navigator.hardwareConcurrency ?? 4;
  const mem = navigator.deviceMemory; // undefined on Firefox — don't assume weak
  if (cores <= 4) return true;
  if (typeof mem === 'number' && mem <= 4) return true;
  return false;
}

function saveDataOn(): boolean {
  return !!navigator.connection?.saveData;
}

function slowNetwork(): boolean {
  const t = navigator.connection?.effectiveType;
  return t === 'slow-2g' || t === '2g' || t === '3g';
}

function hasWebGL(): boolean {
  try {
    const c = document.createElement('canvas');
    return !!(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

/** Decide once at boot — keep cheap and synchronous. */
export function detectPerfTier(): PerfTier {
  if (typeof window === 'undefined') return 'minimal';
  if (prefersReducedMotion() || saveDataOn() || isCoarseMobile()) return 'minimal';
  if (hasWeakHardware() || slowNetwork() || !hasWebGL()) return 'low';
  return 'high';
}

export const PERF_BUDGET = {
  high: {
    dust: 160,
    globePoints: 72,
    shards: 16,
    knotTubular: 48,
    knotRadial: 6,
    ringPoints: 64,
    dprMax: 1.25,
    antialias: false,
    grain: false,
    cursor: true,
    scene: true,
  },
  low: {
    dust: 0,
    globePoints: 0,
    shards: 0,
    knotTubular: 0,
    knotRadial: 0,
    ringPoints: 0,
    dprMax: 1,
    antialias: false,
    grain: false,
    cursor: false,
    scene: false, // gradient only — three.js never loaded
  },
  minimal: {
    dust: 0,
    globePoints: 0,
    shards: 0,
    knotTubular: 0,
    knotRadial: 0,
    ringPoints: 0,
    dprMax: 1,
    antialias: false,
    grain: false,
    cursor: false,
    scene: false,
  },
} as const;

export type PerfBudget = (typeof PERF_BUDGET)[PerfTier];
